import {
  type Alert,
  effectiveRiskLevel,
  formatDateTime,
  type NoticeObligation,
  noticeText,
  RISK_LEVELS,
} from "@pcj/shared";
import { RotateCcw, ShieldAlert, ShieldCheck, TriangleAlert, X } from "lucide-react";
import { useState } from "react";
import { Banner, Button, Card, CopyButton, EmptyState, Pill, SectionTitle } from "../../components/ui";
import { errorMessage } from "../../lib/api";
import { ALERT_TITLES, alertDetail, DEADLINE_TONES, deadlineLabel } from "../../lib/format";
import { useCompany } from "./CompanyLayout";
import { FilingDialog } from "./FilingDialog";

const RISK_CARD = {
  low: { label: "Riesgo bajo", color: "text-emerald-600" },
  medium: { label: "Riesgo medio", color: "text-amber-600" },
  high: { label: "Riesgo alto", color: "text-rose-600" },
} as const;

export function AlertsTab() {
  const state = useCompany();
  const { alerts, clients, clientById, dismissedAlerts, can, actions, today } = state;
  const [filing, setFiling] = useState<NoticeObligation | null>(null);
  const manage = can("manageCompliance");

  const byLevel = { low: 0, medium: 0, high: 0 };
  for (const client of clients) byLevel[effectiveRiskLevel(client)]++;

  function dismiss(alert: Alert) {
    const reason = window.prompt("Motivo para descartar la alerta (queda en la bitácora):", "");
    if (reason === null) return;
    actions.dismissAlert.mutate({ alertId: alert.id, reason: reason || undefined });
  }

  return (
    <div className="space-y-5">
      <SectionTitle
        title="Riesgo y alertas"
        subtitle="Enfoque basado en riesgo, avisos y efectivo · funciones 3 a 5 del art. 41 RCG"
      />

      <div className="grid grid-cols-3 gap-3">
        {RISK_LEVELS.map((level) => (
          <Card key={level} className="p-4 text-center">
            <p className={`text-2xl font-semibold ${RISK_CARD[level].color}`}>{byLevel[level]}</p>
            <p className="mt-1 text-xs uppercase tracking-wide text-gray-500">{RISK_CARD[level].label}</p>
          </Card>
        ))}
      </div>

      {(actions.dismissAlert.error || actions.restoreAlert.error) && (
        <Banner tone="error">{errorMessage(actions.dismissAlert.error ?? actions.restoreAlert.error)}</Banner>
      )}

      {alerts.length === 0 ? (
        <EmptyState icon={ShieldCheck} title="No hay alertas activas" />
      ) : (
        <ul className="space-y-2">
          {alerts.map((alert) => {
            const client = clientById.get(alert.clientId);
            const notice = "notice" in alert ? alert.notice : null;
            return (
              <li key={alert.id}>
                <Card className="flex flex-col gap-3 p-4 sm:flex-row sm:items-start">
                  <TriangleAlert
                    className={`mt-0.5 h-5 w-5 shrink-0 ${alert.severity === "high" ? "text-rose-500" : "text-amber-500"}`}
                    aria-hidden
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-semibold text-gray-900">{ALERT_TITLES[alert.type]}</p>
                      <Pill tone={alert.severity === "high" ? "red" : "amber"}>{client?.name ?? "Cliente"}</Pill>
                      {"deadlineStatus" in alert && notice && (
                        <Pill tone={DEADLINE_TONES[alert.deadlineStatus]}>
                          {deadlineLabel(alert.deadlineStatus, notice.deadline, today)}
                        </Pill>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-gray-600">{alertDetail(alert, client)}</p>
                  </div>
                  <div className="flex shrink-0 flex-wrap gap-2">
                    {notice && client && (
                      <CopyButton
                        label="Copiar datos del Aviso"
                        text={noticeText({ notice, client, company: state.company, operations: state.operations })}
                      />
                    )}
                    {notice && manage && (
                      <Button size="sm" onClick={() => setFiling(notice)}>
                        Marcar presentado
                      </Button>
                    )}
                    {manage && (
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={X}
                        onClick={() => dismiss(alert)}
                        aria-label="Descartar alerta"
                      >
                        Descartar
                      </Button>
                    )}
                  </div>
                </Card>
              </li>
            );
          })}
        </ul>
      )}

      {dismissedAlerts.length > 0 && (
        <details className="rounded-xl border border-gray-200 bg-white p-4">
          <summary className="cursor-pointer text-sm font-medium text-gray-700">
            Alertas descartadas ({dismissedAlerts.length})
          </summary>
          <ul className="mt-3 divide-y divide-gray-100 text-sm">
            {dismissedAlerts.map((d) => (
              <li key={d.alertId} className="flex items-center justify-between gap-3 py-2">
                <div>
                  <p className="font-mono text-xs text-gray-500">{d.alertId}</p>
                  <p className="text-gray-700">{d.reason ?? "Sin motivo registrado"}</p>
                  <p className="text-xs text-gray-400">{formatDateTime(d.dismissedAt)}</p>
                </div>
                {manage && (
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={RotateCcw}
                    onClick={() => actions.restoreAlert.mutate(d.alertId)}
                  >
                    Restaurar
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </details>
      )}

      <Banner tone="info">
        <ShieldAlert className="mr-1 inline h-4 w-4" aria-hidden />
        El envío a la UIF es manual a propósito: no existe una API pública del portal del SAT. Usa «Copiar datos del
        Aviso» y captúralos en el portal; después marca el aviso como presentado con su acuse.
      </Banner>

      <FilingDialog
        open={filing !== null}
        title="Registrar Aviso presentado"
        description="Registra el acuse que entrega el portal del SAT. La alerta desaparecerá y el aviso quedará como presentado."
        pending={actions.updateOperation.isPending}
        error={actions.updateOperation.error}
        onClose={() => setFiling(null)}
        onConfirm={(folio) =>
          filing &&
          actions.updateOperation.mutate(
            { id: filing.triggerOperationId, noticeFiled: true, noticeFolio: folio },
            { onSuccess: () => setFiling(null) },
          )
        }
      />
    </div>
  );
}
